/**
 * Utility functions for formatting numbers, currency, percentages, dates, and durations
 */

import { format, intervalToDuration } from "date-fns";

/**
 * Formats a number as USD currency
 * @param amount - The amount to format
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number): string {
  if (isNaN(amount) || !isFinite(amount)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Formats a number with locale-specific formatting
 * @param num - The number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  if (isNaN(num) || !isFinite(num)) {
    return "0";
  }
  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * Formats a number as a percentage with 2 decimal places
 * @param num - The number to format as percentage
 * @returns Formatted percentage string
 */
export function formatPercentage(num: number): string {
  if (isNaN(num) || !isFinite(num)) {
    return "0.00%";
  }
  return `${num.toFixed(2)}%`;
}

/**
 * Safely converts various timestamp formats to JavaScript Date objects
 * @param timestamp - The timestamp to convert (Firestore Timestamp, Date, number, or string)
 * @returns Date object or null if conversion fails
 */
export function timestampToDate(
  timestamp:
    | { seconds: number; nanoseconds: number }
    | Date
    | number
    | string
    | null
    | undefined,
): Date | null {
  if (!timestamp) return null;

  // If it's already a Date object, return it
  if (timestamp instanceof Date) return timestamp;

  // If it's a Firestore Timestamp with seconds and nanoseconds
  if (
    typeof timestamp === "object" &&
    "seconds" in timestamp &&
    "nanoseconds" in timestamp
  ) {
    return new Date(
      timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000),
    );
  }

  // If it's a number (milliseconds), convert it
  if (typeof timestamp === "number") {
    return new Date(timestamp);
  }

  // Try to parse as string
  if (typeof timestamp === "string") {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * Formats a duration in milliseconds to a human-readable string
 * @param durationMs - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2d 3h", "5h", "30m")
 */
export function formatDuration(durationMs: number | undefined): string {
  if (!durationMs || durationMs < 0) return "--";

  const {
    days = 0,
    hours = 0,
    minutes = 0,
  } = intervalToDuration({
    start: 0,
    end: durationMs,
  });

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * Formats a timestamp to a human-readable date string
 * @param timestamp - The timestamp to format
 * @param formatString - The format string (default: "MMM d, yyyy 'at' h:mm a")
 * @returns Formatted date string or null if timestamp is invalid
 */
export function formatTimestamp(
  timestamp:
    | { seconds: number; nanoseconds: number }
    | Date
    | number
    | string
    | null
    | undefined,
  formatString: string = "MMM d, yyyy 'at' h:mm a",
): string | null {
  const date = timestampToDate(timestamp);
  return date ? format(date, formatString) : null;
}

/**
 * Formats a timestamp as a date-only value without local timezone day drift.
 */
export function formatDateOnly(
  timestamp:
    | { seconds: number; nanoseconds: number }
    | Date
    | number
    | string
    | null
    | undefined,
): string | null {
  const date = timestampToDate(timestamp);
  return date ? date.toISOString().slice(0, 10) : null;
}

/**
 * Formats a date relative to now (e.g., "2 hours ago", "in 3 days")
 * @param value - The date to format
 * @returns Relative time string or "-" for empty values
 */
export function formatRelativeTimestamp(value: Date | undefined): string {
  if (!value) return "-";

  const timestamp = new Date(value).getTime();
  const now = Date.now();
  const diff = timestamp - now;
  const absDiff = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  if (absDiff < 60_000) {
    return rtf.format(Math.round(diff / 1000), "second");
  }
  if (absDiff < 3_600_000) {
    return rtf.format(Math.round(diff / 60_000), "minute");
  }
  if (absDiff < 86_400_000) {
    return rtf.format(Math.round(diff / 3_600_000), "hour");
  }
  if (absDiff < 604_800_000) {
    return rtf.format(Math.round(diff / 86_400_000), "day");
  }
  if (absDiff < 2_629_746_000) {
    return rtf.format(Math.round(diff / 604_800_000), "week");
  }
  if (absDiff < 31_556_952_000) {
    return rtf.format(Math.round(diff / 2_629_746_000), "month");
  }
  return rtf.format(Math.round(diff / 31_556_952_000), "year");
}
